import { ChunkErrorSessionManager } from './ChunkErrorSessionManager';
import { ChunkErrorRecovery } from './ChunkErrorRecovery';
import { extractModulePathFromImporter } from './RetryStrategies';
import { logError, logEvent } from './logger-utils';
import type { RetryConfig } from './RetryConfig';

export class DynamicImportRetry {
  private readonly errorRecovery: ChunkErrorRecovery;

  constructor(private readonly config: RetryConfig) {
    this.errorRecovery = new ChunkErrorRecovery(config);
  }

  async executeWithRetry<T>(importer: () => Promise<T>): Promise<T> {
    try {
      const result = await importer();
      ChunkErrorSessionManager.clearErrorCount();
      return result;
    } catch (error: any) {
      return this.handleImportError(error, importer);
    }
  }

  private async handleImportError<T>(error: Error, importer: () => Promise<T>): Promise<T> {
    logError(error);
    
    const state = ChunkErrorSessionManager.getState();
    if (state.isPostReload) {
      logEvent({
        message: 'Chunk loading failed after page reload, redirecting to error page',
        level: 'error',
        extra: { postReload: true, errorMessage: error.message },
      });
      return this.errorRecovery.handleError(error);
    }

    const modulePath = await this.extractModulePath(error, importer);
    
    if (!modulePath) {
      logEvent({
        message: 'Unable to determine module path for retry',
        level: 'error',
        extra: { error: error.message },
      });
      return this.errorRecovery.handleError(error);
    }

    return this.attemptRetries(modulePath);
  }

  private async extractModulePath(error: Error, importer: () => any): Promise<string | null> {
    return extractModulePathFromImporter(importer);
  }

  private async attemptRetries<T>(modulePath: string): Promise<T> {
    const importFn = this.config.importFunction!;

    for (let i = 0; i < this.config.maxRetries; i++) {
      const cacheBustedPath = this.createCacheBustedPath(modulePath);
      
      try {
        const result = await importFn(cacheBustedPath);
        
        this.logRetrySuccess(i + 1, cacheBustedPath);
        ChunkErrorSessionManager.clearErrorCount();
        return result;
      } catch (retryError: any) {
        this.logRetryFailure(i + 1, retryError);
        // Skip the backoff after the final attempt — otherwise we wait the full
        // baseRetryDelay * 2**(maxRetries-1) (~16s at defaults) for nothing before giving up.
        if (i < this.config.maxRetries - 1) {
          await this.waitBeforeNextRetry(i);
        }
      }
    }
    
    return this.handleExhaustedRetries(modulePath);
  }

  private createCacheBustedPath(modulePath: string): string {
    return `${modulePath}?t=${Date.now()}`;
  }

  private logRetrySuccess(attempt: number, cacheBustedPath: string): void {
    logEvent({
      message: 'Asset retry loading successful',
      level: 'info',
      extra: { retryCount: attempt, url: cacheBustedPath },
    });
  }

  private logRetryFailure(attempt: number, retryError: Error): void {
    logEvent({
      message: `Asset loading exception ${attempt}`,
      level: 'warning',
      extra: { error: retryError },
    });
    logError(retryError);
  }

  private async waitBeforeNextRetry(attemptIndex: number): Promise<void> {
    const delay = this.config.baseRetryDelay * (2 ** attemptIndex);
    return new Promise(resolve => setTimeout(resolve, delay));
  }

  private async handleExhaustedRetries<T>(modulePath: string): Promise<T> {
    logEvent({
      message: `All retry attempts exhausted for module: ${modulePath}`,
      level: 'error',
      extra: { 
        modulePath, 
        maxRetries: this.config.maxRetries 
      },
    });
    
    return this.errorRecovery.handleError(new Error(`Failed to load module: ${modulePath}`));
  }
}